/*
 * Copyright (c) 2018 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, intlShape, injectIntl, defineMessages} from 'react-intl';
import Cookies from 'universal-cookie';
import {Button, FormGroup, ControlLabel, FormControl, Modal, resolveLocale} from 'eui-components';

const messages = defineMessages({
  csHoverText: {
    id: 'LanguageSelector.cs.native.hover',
    description: 'Native language hover text for Czech selection.',
    defaultMessage: 'Czech'
  },
  csSelectText: {
    id: 'LanguageSelector.cs.select.text',
    description: 'Czech language text for dropdown.',
    defaultMessage: 'Čeština'
  },
  deHoverText: {
    id: 'LanguageSelector.de.native.hover',
    description: 'Native language hover text for German selection.',
    defaultMessage: 'German'
  },
  deSelectText: {
    id: 'LanguageSelector.de.select.text',
    description: 'German language text for dropdown.',
    defaultMessage: 'Deutsch'
  },
  enHoverText: {
    id: 'LanguageSelector.en.native.hover',
    description: 'Native language hover text for English selection.',
    defaultMessage: 'English'
  },
  enSelectText: {
    id: 'LanguageSelector.en.select.text',
    description: 'English language text for dropdown.',
    defaultMessage: 'English'
  },
  esHoverText: {
    id: 'LanguageSelector.es.native.hover',
    description: 'Native language hover text for Spanish selection.',
    defaultMessage: 'Spanish'
  },
  esSelectText: {
    id: 'LanguageSelector.es.select.text',
    description: 'Spanish language text for dropdown.',
    defaultMessage: 'Español'
  },
  frHoverText: {
    id: 'LanguageSelector.fr.native.hover',
    description: 'Native language hover text for French selection.',
    defaultMessage: 'French'
  },
  frSelectText: {
    id: 'LanguageSelector.fr.select.text',
    description: 'French language text for dropdown.',
    defaultMessage: 'Français'
  },
  itHoverText: {
    id: 'LanguageSelector.it.native.hover',
    description: 'Native language hover text for Italian selection.',
    defaultMessage: 'Italian'
  },
  itSelectText: {
    id: 'LanguageSelector.it.select.text',
    description: 'Italian language text for dropdown.',
    defaultMessage: 'Italiano'
  },
  miHoverText: {
    id: 'LanguageSelector.mi.native.hover',
    description: 'Native language hover text for Maori selection.',
    defaultMessage: 'Maori'
  },
  miSelectText: {
    id: 'LanguageSelector.mi.select.text',
    description: 'Maori language text for dropdown.',
    defaultMessage: 'Māori'
  },
  nlHoverText: {
    id: 'LanguageSelector.nl.native.hover',
    description: 'Native language hover text for Dutch selection.',
    defaultMessage: 'Dutch'
  },
  nlSelectText: {
    id: 'LanguageSelector.nl.select.text',
    description: 'Dutch language text for dropdown.',
    defaultMessage: 'Nederlands'
  },
  ptHoverText: {
    id: 'LanguageSelector.pt.native.hover',
    description: 'Native language hover text for Portuguese selection.',
    defaultMessage: 'Portuguese'
  },
  ptSelectText: {
    id: 'LanguageSelector.pt.select.text',
    description: 'Portuguese language text for dropdown.',
    defaultMessage: 'Português'
  },
  svHoverText: {
    id: 'LanguageSelector.sv.native.hover',
    description: 'Native language hover text for Swedish selection.',
    defaultMessage: 'Swedish'
  },
  svSelectText: {
    id: 'LanguageSelector.sv.select.text',
    description: 'Swedish language text for dropdown.',
    defaultMessage: 'Svenska'
  },
  zhHansHoverText: {
    id: 'LanguageSelector.zh-Hans.native.hover',
    description: 'Native language hover text for Chinese (Simplified) selection.',
    defaultMessage: 'Chinese (Simplified)'
  },
  zhHansSelectText: {
    id: 'LanguageSelector.zh-Hans.select.text',
    description: 'Chinese (Simplified) language text for dropdown.',
    defaultMessage: '中文(简体)'
  },
  zhHantHoverText: {
    id: 'LanguageSelector.zh-Hant.native.hover',
    description: 'Native language hover text for Chinese (Traditional) selection.',
    defaultMessage: 'Chinese (Traditional)'
  },
  zhHantSelectText: {
    id: 'LanguageSelector.zh-Hant.select.text',
    description: 'Chinese (Traditional) language text for dropdown.',
    defaultMessage: '中文(繁體)'
  },
  jaHoverText: {
    id: 'LanguageSelector.ja.native.hover',
    description: 'Native language hover text for Japanese selection.',
    defaultMessage: 'Japanese'
  },
  jaSelectText: {
    id: 'LanguageSelector.ja.select.text',
    description: 'Japanese language text for dropdown.',
    defaultMessage: '日本語'
  },
  koHoverText: {
    id: 'LanguageSelector.ko.native.hover',
    description: 'Native language hover text for Korean selection.',
    defaultMessage: 'Korean'
  },
  koSelectText: {
    id: 'LanguageSelector.ko.select.text',
    description: 'Korean language text for dropdown.',
    defaultMessage: '한국어'
  },
  thHoverText: {
    id: 'LanguageSelector.th.native.hover',
    description: 'Native language hover text for Thai selection.',
    defaultMessage: 'Thai'
  },
  thSelectText: {
    id: 'LanguageSelector.th.select.text',
    description: 'Thai language text for dropdown.',
    defaultMessage: 'ภาษาไทย'
  }
});

/* A year in seconds */
const cookieMaxAge = 60 * 60 * 24 * 365;

class LanguageSelector extends Component {
    static propTypes = {
      intl: intlShape.isRequired,
      history: PropTypes.shape({
        goBack: PropTypes.func
      })
    };

    constructor (props) {
      super(props);
      this.cookies = new Cookies();
      this.state = {
        inputValue: (this.cookies.get('locale') ? resolveLocale() : 'en'),
        show: true
      };
    }

    onHide = e => {
      this.setState({show: false});
      this.props.history.goBack();
    };

    handleLocaleChange = (e) => {

      e.preventDefault();

      this.setState({inputValue: e.target.value, show: false});

      this.cookies.set('locale', e.target.value, {maxAge: cookieMaxAge});
      window.location.assign(process.env.PUBLIC_URL || '/')
    };

    componentDidMount () {
      this.setState({inputValue: this.cookies.get('locale') ? resolveLocale() : 'en'});
    }

    render () {
      // debugger;
      return <Modal show={this.state.show} onHide={this.onHide} backdrop="static" bsSize="sm">
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage id="LanguageSelector.modalTitle" defaultMessage="Select a Language" />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <FormGroup controlId="locale-value">
              <ControlLabel><FormattedMessage id="LanguageSelector.modalTitle" defaultMessage="Select a Language"/></ControlLabel>
              <FormControl componentClass="select" value={this.state.inputValue} onChange={this.handleLocaleChange}>
                <option value="cs" title={this.props.intl.formatMessage(messages.csHoverText)}>
                  {this.props.intl.formatMessage(messages.csSelectText)}
                </option>
                <option value="de" title={this.props.intl.formatMessage(messages.deHoverText)}>
                  {this.props.intl.formatMessage(messages.deSelectText)}
                </option>
                <option value="en" title={this.props.intl.formatMessage(messages.enHoverText)}>
                  {this.props.intl.formatMessage(messages.enSelectText)}
                </option>
                <option value="es" title={this.props.intl.formatMessage(messages.esHoverText)}>
                  {this.props.intl.formatMessage(messages.esSelectText)}
                </option>
                <option value="fr" title={this.props.intl.formatMessage(messages.frHoverText)}>
                  {this.props.intl.formatMessage(messages.frSelectText)}
                </option>
                <option value="it" title={this.props.intl.formatMessage(messages.itHoverText)}>
                  {this.props.intl.formatMessage(messages.itSelectText)}
                </option>
                <option value="mi" title={this.props.intl.formatMessage(messages.miHoverText)}>
                  {this.props.intl.formatMessage(messages.miSelectText)}
                </option>
                <option value="nl" title={this.props.intl.formatMessage(messages.nlHoverText)}>
                  {this.props.intl.formatMessage(messages.nlSelectText)}
                </option>
                <option value="pt" title={this.props.intl.formatMessage(messages.ptHoverText)}>
                  {this.props.intl.formatMessage(messages.ptSelectText)}
                </option>
                <option value="sv" title={this.props.intl.formatMessage(messages.svHoverText)}>
                  {this.props.intl.formatMessage(messages.svSelectText)}
                </option>
                <option value="zh-Hans" title={this.props.intl.formatMessage(messages.zhHansHoverText)}>
                  {this.props.intl.formatMessage(messages.zhHansSelectText)}
                </option>
                <option value="zh-Hant" title={this.props.intl.formatMessage(messages.zhHantHoverText)}>
                  {this.props.intl.formatMessage(messages.zhHantSelectText)}
                </option>
                <option value="ja" title={this.props.intl.formatMessage(messages.jaHoverText)}>
                  {this.props.intl.formatMessage(messages.jaSelectText)}
                </option>
                <option value="ko" title={this.props.intl.formatMessage(messages.koHoverText)}>
                  {this.props.intl.formatMessage(messages.koSelectText)}
                </option>
                <option value="th" title={this.props.intl.formatMessage(messages.thHoverText)}>
                  {this.props.intl.formatMessage(messages.thSelectText)}
                </option>
              </FormControl>
            </FormGroup>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.onHide}>
            <FormattedMessage id="LanguageSelector.cancelButtonLabel"
              defaultMessage="Cancel"/>
          </Button>
        </Modal.Footer>
      </Modal>;
    }
}

export default injectIntl(LanguageSelector);



// WEBPACK FOOTER //
// ./src/LanguageSelector/LanguageSelector.js